import React, { useEffect, useRef, useState } from "react";

import { Button, Spin } from "antd";
import { useHistory } from "react-router-dom";

import PaymentPendingBanner from "../../grace-period/components/PaymentPendingBanner";
import { useAccessStatus } from "../../grace-period/hooks/useAccessStatus";
import UpdateOnboardingModal from "../../Onboarding/UpdateOnboardingModal/UpdateOnboardingModal";
import CreateSampleModal from "../CreateSampleModal/CreateSampleModal";
import Drafts from "./Drafts/Drafts";
import NoTestCreditsBanner from "./NoTestCreditsBanner";
import NoTestCreditsEmptyState from "./NoTestCreditsEmptyState";
import SampleSubmissionLandingState from "./SampleSubmissionLandingState";
import Submissions from "./Submissions/Submissions";

import { canAccessSampleSubmissionTestDirectory } from "../../../utils/Permissions";
import {
  isPrepaidOrSubscriptionCustomer,
  parseCreditAmount,
} from "../../MyAccount/UsageMetrics/utils";
import { TEST_DIRECTORY_PATH } from "../utils";

import { ReactComponent as TubeIcon } from "../../../assets/icons/Sample Submission Redesign/test-tube-line.svg";
import { ReactComponent as ChevronDown } from "../../../assets/images/common/ChevronDown.svg";

import "../SampleSubmission.css";
import "./MainPage.css";

export default function MainPage(props) {
  const {
    setShowSubmissionForm,
    setDraftEditing,
    setSubmissionEditingSample,
    setSubmissionAddingSamples,
    fieldsError,
    setSampleCategory,
    setSampleFieldLoaded,
    loadingFields,
  } = props;

  const [submittedSubmissions, setSubmittedSubmissions] = useState([]);
  const [draftSubmissions, setDraftSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(true);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [createSubmission, setCreateSubmission] = useState(false);
  const [isDraftDrawerOpen, setIsDraftDrawerOpen] = useState(false);
  const [showUpdateOnboardingModal, setShowUpdateOnboardingModal] = useState(false);
  const [hasDraftsGlobally, setHasDraftsGlobally] = useState(false);
  const { customer_type, total_credits, hasLoadedAccessStatus } = useAccessStatus();
  const history = useHistory();
  const showTestDirectory = canAccessSampleSubmissionTestDirectory();
  const isInitialData = useRef(true);

  useEffect(() => {
    if (fieldsError) {
      setLoadingDrafts(false);
      setLoadingSubmissions(false);
    }
  }, [fieldsError]);

  const loadingBothLists = loadingSubmissions || loadingDrafts;
  const noSubmissionAndDraftData = !loadingBothLists && isInitialData.current && submittedSubmissions.length === 0 && !hasDraftsGlobally;

  const handleCreateSubmission = () => {
    setCreateSubmission(false);
  };

  const showNoTestCreditsEmptyState = (
    isPrepaidOrSubscriptionCustomer(customer_type)
    && parseCreditAmount(total_credits) === 0
  );
  const showNoCreditsPage = showNoTestCreditsEmptyState && noSubmissionAndDraftData;
  const showNoCreditsBanner = showNoTestCreditsEmptyState && !loadingBothLists && !noSubmissionAndDraftData;

  if (!hasLoadedAccessStatus) {
    return (
      <div className="sample-submission-background main MainPageLayout">
        <PaymentPendingBanner />
        <div className="sample-submission-main-container sample-submission-main-container-flex">
          <div className="sample-submission-create-new-in-center">
            <Spin size="large" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="sample-submission-background main MainPageLayout">
        <PaymentPendingBanner />
        {showNoCreditsBanner && <NoTestCreditsBanner />}
        <div className={`sample-submission-main-container ${noSubmissionAndDraftData ? "sample-submission-main-container-flex" : ""}`}>
          {showNoCreditsPage && (
            <NoTestCreditsEmptyState />
          )}
          {noSubmissionAndDraftData && !showNoCreditsPage && (
            <SampleSubmissionLandingState
              onCreateSubmission={() => setCreateSubmission(true)}
              onUploadExcel={() => setShowUpdateOnboardingModal(true)}
            />
          )}
          {!noSubmissionAndDraftData && (
            <>
              <div className={`submissions-list ${showTestDirectory ? "submissions-list--with-test-directory" : ""}`}>
                <Submissions
                  setShowSubmissionForm={setShowSubmissionForm}
                  setSampleFieldLoaded={setSampleFieldLoaded}
                  setSubmissionEditingSample={setSubmissionEditingSample}
                  setSubmissionAddingSamples={setSubmissionAddingSamples}
                  setDraftEditing={setDraftEditing}
                  loadingSubmissions={loadingSubmissions}
                  setLoadingSubmissions={setLoadingSubmissions}
                  submittedSubmissions={submittedSubmissions}
                  setSubmittedSubmissions={setSubmittedSubmissions}
                  isInitialData={isInitialData}
                  setSampleCategory={setSampleCategory}
                  setShowUpdateOnboardingModal={setShowUpdateOnboardingModal}
                />

                <Drafts
                  setShowSubmissionForm={setShowSubmissionForm}
                  setSampleFieldLoaded={setSampleFieldLoaded}
                  setDraftEditing={setDraftEditing}
                  loadingDrafts={loadingDrafts}
                  setLoadingDrafts={setLoadingDrafts}
                  draftSubmissions={draftSubmissions}
                  setDraftSubmissions={setDraftSubmissions}
                  isDraftDrawerOpen={isDraftDrawerOpen}
                  closeDraftDrawer={() => setIsDraftDrawerOpen(false)}
                  setHasDraftsGlobally={setHasDraftsGlobally}
                />

                <div className="MainPage_SideActions">
                  <Button
                    className="MainPage_DraftToggle"
                    htmlType="button"
                    onClick={() => setIsDraftDrawerOpen(true)}
                  >
                    <ChevronDown className="MainPage_DraftToggleIcon" />
                    <span>Drafts</span>
                  </Button>
                  {showTestDirectory && (
                    <Button
                      className="MainPage_TestDirectoryButton"
                      htmlType="button"
                      aria-label="Open Test Directory"
                      onClick={() => history.push(TEST_DIRECTORY_PATH)}
                    >
                      <TubeIcon width={14} height={14} aria-hidden />
                      <span>Test Directory</span>
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
          {
            createSubmission && (
              <CreateSampleModal
                setShowSubmissionForm={setShowSubmissionForm}
                onCancel={handleCreateSubmission}
                setSampleCategory={setSampleCategory}
                heading="Create New Sample Submission "
                loadingFields={loadingFields}
              />
            )
          }
        </div>
      </div>
      {showUpdateOnboardingModal && (
        <UpdateOnboardingModal
          open={showUpdateOnboardingModal}
          onClose={() => setShowUpdateOnboardingModal(false)}
        />
      )}
    </>
  );
}
